/**
 * Presentational states for the Practice Schedule PP-login gate. Pure
 * presentation — all decisioning lives in `useScheduleAccessGate`.
 *
 * A schedule-specific copy of worklist/WorklistGateStates.tsx's
 * `PpLoginRequiredState`/`GateShell`, rather than a reuse: that component's
 * copy is worklist-specific ("your priority worklist", "ranked patient
 * worklist") and it exposes no text props, so the schedule needs its own
 * wording. The styling deliberately mirrors it (same semantic-token
 * conventions) so both gates read identically.
 */

function GateShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="h-full min-h-0 flex flex-col items-center justify-center px-6 py-16">
      <div className="max-w-md w-full text-center rounded-2xl border border-dashed border-border bg-card/50 p-8 flex flex-col items-center gap-4">
        {children}
      </div>
    </div>
  );
}

export interface SchedulePpLoginRequiredStateProps {
  identityEmail: string | undefined;
  onLogin: () => void;
}

/** Shown when the provider holds no Provider Portal bearer yet. */
export function SchedulePpLoginRequiredState({
  identityEmail,
  onLogin,
}: SchedulePpLoginRequiredStateProps) {
  return (
    <GateShell>
      <h2 className="text-lg font-bold text-foreground m-0">
        Log in to Provider Portal to view the practice schedule
      </h2>
      <p className="text-sm text-muted-foreground m-0">
        The practice schedule is powered by Provider Portal and is scoped to the
        providers you can access. Sign in to continue.
      </p>
      {identityEmail ? (
        <button
          type="button"
          onClick={onLogin}
          className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          Log in to Provider Portal
        </button>
      ) : (
        <p role="alert" className="text-sm text-destructive font-medium m-0">
          Unable to determine your sign-in identity — please refresh and try again.
        </p>
      )}
    </GateShell>
  );
}

/** Shown while a persisted PP link is being silently refreshed. */
export function ScheduleResolvingState() {
  return (
    <GateShell>
      <div
        className="h-8 w-8 rounded-full border-2 border-border border-t-primary animate-spin"
        role="status"
        aria-label="Loading the practice schedule"
      />
      <p className="text-sm text-muted-foreground m-0">Loading the practice schedule…</p>
    </GateShell>
  );
}
